import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ColDef } from 'ag-grid-community/dist/lib/entities/colDef';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  AggFunc,
  BaseComponent,
  BaseEditFormComponent,
  FormatService,
  GridRowSelect,
  GridService,
  PurchaseOrderLinePurchasesAllocationsSummaryTop,
  PurchaseOrderLineService,
  PurchaseOrderPurchaseAllocation,
  PurchaseOrderSalesAllocation,
  SalesOrderLineService,
  SummaryItem,
} from 'tms-lib';
import { PurchaseOrderPurchaseAllocationService, PurchaseOrderService } from '../../../services';

@Component({
  selector: 'app-purchase-order-line-purchase-order-allocation',
  templateUrl: './purchase-order-line-purchase-order-allocation.component.html',
  styleUrl: './purchase-order-line-purchase-order-allocation.component.scss',
})
export class PurchaseOrderLinePurchaseOrderAllocationComponent extends BaseComponent implements OnInit {
  @ViewChild(BaseEditFormComponent) form: BaseEditFormComponent;
  @Input() purchaseOrderLineId: string;
  @Input() header: string;
  @Input() gridCssClass = 'tab-grid';
  @Output() modelUpdated = new EventEmitter();

  model$: Observable<PurchaseOrderLinePurchasesAllocationsSummaryTop>;
  purchaseOrderLines$: Observable<PurchaseOrderPurchaseAllocation[]>;

  columnDefs: ColDef[] = [
    ...this.gridService.checkboxRowSelect('orderNo', $localize`Purchase Order Number`, undefined, undefined, {
      abbreviatedHeaderName: 'PO Number',
    }),
    this.gridService.text('supplierKey', $localize`Supplier`),
    this.gridService.text('supplierName', $localize`Name`),
    this.gridService.text('productKey', $localize`Product`),
    this.gridService.text('productDescription', $localize`Description`),
    this.gridService.text('balance', $localize`Current Quantity`, { aggFunc: AggFunc.sum }),
    this.gridService.date('deliveryDate', $localize`Delivery Date`),
    this.gridService.numberFormat('totalAllocated', $localize`Total Allocated`, { aggFunc: AggFunc.sum }),
    this.gridService.editableField(
      'allocated',
      $localize`This Allocation`,
      (model: PurchaseOrderSalesAllocation) => this.allocatedEdited(model),
      (model: PurchaseOrderSalesAllocation) => +model.allocated > 0,
      { aggFunc: AggFunc.sum },
    ),
    this.gridService.text('deliveryAddressName', $localize`Delivery Name`),
    this.gridService.text('deliveryAddress', $localize`Delivery Address`),
  ];

  constructor(
    private purchaseOrderLineService: PurchaseOrderLineService,
    private purchaseOrderPurchaseAllocationService: PurchaseOrderPurchaseAllocationService,
    private purchaseOrderService: PurchaseOrderService,
    private gridService: GridService,
    private formatService: FormatService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.getModel();
  }

  summaryItems(model: PurchaseOrderLinePurchasesAllocationsSummaryTop): SummaryItem[] {
    return [
      { label: $localize`Product`, value: model.productKey },
      { label: $localize`Description`, value: model.productDescription },
      { label: $localize`Quantity`, value: model.quantity },
      { label: $localize`Allocated`, value: model.allocated },
      { label: $localize`Delivery Date`, value: this.formatService.date(model.deliveryDate) },
    ];
  }

  getModel() {
    this.model$ = this.purchaseOrderLineService.purchaseAllocationsSummaryTop(this.purchaseOrderLineId);
    this.purchaseOrderLines$ = this.purchaseOrderLineService.purchaseOrderPurchaseAllocation(this.purchaseOrderLineId);
  }

  updateSuccess() {
    this.getModel();
    this.modelUpdated.emit();

    this.purchaseOrderService.refreshDetails.next();
    this.purchaseOrderLineService.refreshDetails.next();
    this.purchaseOrderPurchaseAllocationService.refreshDetails.next();
  }

  rowSelected(event: GridRowSelect) {
    const allocateOrDeallocate = event.selected
      ? this.purchaseOrderPurchaseAllocationService.allocate(this.purchaseOrderLineId, event.model.salesOrderLineId)
      : this.purchaseOrderPurchaseAllocationService.delete(event.model.id);

    allocateOrDeallocate.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateSuccess());
  }

  private allocatedEdited(model) {
    this.purchaseOrderPurchaseAllocationService
      .updatePurchaseOrderPurchaseAllocation(model.id, model.allocated)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateSuccess());
  }
}
